"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, Calendar, ArrowRight, CircleCheck as CheckCircle2, MapPin, Sparkles, PhoneCall, Search, Check } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqCategories = [{
  id: "all",
  label: "All Questions"
}, {
  id: "strategy",
  label: "Local Strategy & Maps"
}, {
  id: "process",
  label: "Direct Working & Process"
}, {
  id: "results",
  label: "Timelines & ROI"
}];
const faqData = [{
  id: "item-1",
  category: "strategy",
  question: "How does Local SEO specifically benefit businesses in Peterborough and Cambridgeshire?",
  answer: "Peterborough is one of the UK's fastest-growing cathedral cities, with high commercial expansion across Nene Valley, Cygnet Park, and the city centre. When local customers search for your services, Google displays the 'Local 3-Pack' maps view at the very top. My Peterborough Local SEO strategies optimize your Google Business Profile, local citations, and on-page signals so your business dominates local search queries, driving high-intent phone calls and website enquiries directly from local decision-makers."
}, {
  id: "item-2",
  category: "results",
  question: "How quickly can my Peterborough business expect to see results in Google Maps?",
  answer: "Most Peterborough clients start seeing tangible improvements in Map Pack rankings and local impression share within 4 to 8 weeks. Comprehensive Local SEO involves fixing technical errors, resolving NAP (Name, Address, Phone) inconsistencies across UK directories, creating Peterborough-centric landing pages, and earning high-authority local backlinks. Continuous compounding authority typically delivers sustainable top-3 map placements within 3 to 6 months."
}, {
  id: "item-3",
  category: "process",
  question: "Will I work directly with you, Yassine, or an account manager?",
  answer: "You will work directly with me—Yassine Essaadi. I do not outsource your campaign to junior account executives, offshore teams, or automated agency software. When you hire YE Consultancy, every technical audit, local keyword map, Google Business Profile optimization, and monthly performance review is executed personally by me. You get direct senior consultant access and transparent communication every step of the way."
}, {
  id: "item-4",
  category: "strategy",
  question: "How do you optimize my Google Business Profile for Peterborough search intent?",
  answer: "I perform a comprehensive audit of your Google Business Profile (GBP), refining primary and secondary category selections, geo-targeted service areas, geotagged media assets, and structured Q&As. Furthermore, I implement a systematic review generation framework and embed localized schema markup on your website to ensure Google confidently connects your profile to high-value Peterborough commercial queries."
}, {
  id: "item-5",
  category: "strategy",
  question: "Why is local search optimization critical if I already have a functional website?",
  answer: "A functional website without local search optimization is invisible to local buyers. Over 46% of all Google searches have local intent, and 78% of local mobile searches lead to an offline purchase or inquiry within 24 hours. Without deliberate local schema, geo-targeted content, and high-quality local citation signals, Peterborough competitors will consistently capture the top organic spots and local map positions."
}, {
  id: "item-6",
  category: "strategy",
  question: "Do I need a physical address in Peterborough city centre to rank locally?",
  answer: "No, you do not need a central Peterborough store or office address to rank effectively. Google supports 'Service Area Businesses' (SABs) that deliver services directly to customers across Greater Peterborough, Stamford, Yaxley, Hampton, and surrounding Cambridgeshire locations. I configure your profile to hide your physical home address while maximizing ranking coverage across all target postcode sectors."
}, {
  id: "item-7",
  category: "strategy",
  question: "How do you handle Local SEO for Peterborough businesses targeting nearby areas like Stamford, Huntingdon, or Spalding?",
  answer: "I engineer tailored multi-location SEO structures. This includes creating dedicated, non-duplicative location pages optimized for local intent (e.g., Local SEO in Stamford, Commercial Services in Huntingdon) paired with hyper-local schema markup. This allows your business to rank prominently across the entire A1/A47 regional corridor without triggering Google spam filters."
}, {
  id: "item-8",
  category: "strategy",
  question: "What is the key difference between UK National SEO and Peterborough Local SEO?",
  answer: "National SEO focuses on broad, non-geographical keywords across the UK without map integration. Peterborough Local SEO prioritizes proximity, geographic relevance, Google Maps prominence, local citations, customer reviews, and localized search queries (e.g., 'B2B IT support Peterborough'). Local SEO delivers faster, high-converting commercial leads because searchers actively seek immediate regional solutions."
}, {
  id: "item-9",
  category: "process",
  question: "How do you build authentic local citations and backlinks in Cambridgeshire?",
  answer: "I build clean, authoritative local backlinks and citations from verified UK directories, Cambridgeshire business networks, industry-specific portals, and regional news sources. I never purchase low-quality spam link packages that risk Google penalties. Every link acquired serves to build genuine domain trust and local entity authority."
}, {
  id: "item-10",
  category: "results",
  question: "What metrics do you track to prove Peterborough Local SEO return on investment?",
  answer: "I focus exclusively on business growth metrics: Google Map Pack rankings, organic phone call volume, direction requests, website contact form submissions, and conversion value. Monthly reports cut through vanity metrics to show exactly how search visibility translates into real customer leads and business revenue."
}, {
  id: "item-11",
  category: "results",
  question: "How much does a Peterborough Local SEO campaign with YE Consultancy cost?",
  answer: "Because every business faces different competitive dynamics, I tailor my SEO strategy to your specific goals and target locations. Standard retainers offer clear deliverables with no hidden agency markups or locked-in long-term contracts. During our initial free consultation, I provide a transparent proposal specifying the exact scope required to outrank your Peterborough competitors."
}, {
  id: "item-12",
  category: "process",
  question: "How do we get started with a Peterborough SEO audit and consultation?",
  answer: "Getting started is straightforward. Click 'Book a Free SEO Consultation' to schedule a 30-minute discovery call directly with me. Before we speak, I conduct a preliminary assessment of your Peterborough search visibility and Google Business Profile so our discussion focuses immediately on actionable growth opportunities."
}];
export default function PeterboroughFaq() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = useMemo(() => {
    return faqData.filter(item => {
      const matchesCategory = activeCategory === "all" || item.category === activeCategory;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);
  return <section id="peterboroughlocalseofaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {}
      <div className="absolute top-0 right-0 -z-10 translate-x-1/3 -translate-y-1/4 w-96 h-96 rounded-full bg-secondary/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 -translate-x-1/3 translate-y-1/4 w-96 h-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge variant="secondary" className="mb-4 px-3 py-1 bg-secondary text-secondary-foreground font-medium rounded-full text-xs tracking-wide inline-flex items-center gap-1.5">
            <MapPin className="size-3.5 text-primary" />
            <span>Peterborough Local SEO Expert</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-4">
            Peterborough Local SEO <span className="text-primary">FAQ</span>
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
            Direct, transparent answers from Yassine Essaadi on how to dominate local Google search results and map packs in Peterborough and surrounding Cambridgeshire areas.
          </p>
        </div>

        {}
        <div className="mb-10 max-w-4xl mx-auto space-y-6">
          {}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input type="text" placeholder="Search local SEO questions (e.g., Google Maps, pricing, timing)..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-card text-card-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-[box-shadow,border-color] shadow-xs" />
            {searchQuery && <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
                Clear
              </Button>}
          </div>

          {}
          <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
            {faqCategories.map((cat, idx) => {
            const isActive = activeCategory === cat.id;
            return <Button key={cat.id} data-index={idx} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveCategory(cat.id)} className={`rounded-full text-xs transition-colors duration-200 ${isActive ? "bg-primary text-primary-foreground shadow-xs" : "bg-card text-foreground hover:bg-secondary hover:text-secondary-foreground border-border"}`}>
                  {cat.label}
                </Button>;
          })}
          </div>
        </div>

        {}
        <div className="max-w-4xl mx-auto min-h-[320px]">
          {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="w-full space-y-4">
              {filteredFaqs.map((item, index) => <div data-index={index} key={item.id}>
                  <motion.div initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: index * 0.04
            }}>
                    <AccordionItem value={item.id} className="border border-border bg-card rounded-xl px-5 sm:px-6 shadow-xs transition-[box-shadow,border-color] hover:border-primary/40 data-[state=open]:border-primary data-[state=open]:shadow-md">
                      <AccordionTrigger className="py-5 hover:no-underline text-left cursor-pointer group">
                        <span className="text-base sm:text-lg font-semibold font-serif text-primary group-hover:text-primary/80 transition-colors leading-snug pr-2">
                          {item.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-5 pt-1 text-foreground/90 font-sans text-sm sm:text-base leading-relaxed border-t border-border/50">
                        <div className="pt-3 space-y-3">
                          <p>{item.answer}</p>
                          <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground font-medium">
                            <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                            <span>Direct SEO Strategy by Yassine Essaadi</span>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                </div>)}
            </Accordion> : <div className="text-center py-12 px-4 rounded-xl border border-dashed border-border bg-card">
              <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-semibold font-serif text-foreground mb-1">
                No matching questions found
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Try adjusting your search query or select another category filter.
              </p>
              <Button variant="outline" size="sm" onClick={() => {
            setSearchQuery("");
            setActiveCategory("all");
          }}>
                Reset Filters
              </Button>
            </div>}
        </div>

        {}
        <div className="mt-16 max-w-4xl mx-auto bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2 space-y-2">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="size-4 text-accent" />
                <span>The YE Consultancy Promise</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
                Have a specific Peterborough SEO question not answered here?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You work directly with me—no account handlers or junior staff. Ask me directly about your website technical health, competitors, or local ranking opportunities.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row md:flex-col gap-3 justify-center md:items-end">
              <Button asChild variant="default" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-5 py-2.5 rounded-lg text-sm shadow-xs transition-all">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Book a Free Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" className="w-full sm:w-auto border-border text-foreground hover:bg-muted font-medium px-5 py-2.5 rounded-lg text-sm transition-all">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore Case Studies</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>

          <Separator className="my-6 bg-border" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-muted-foreground font-sans">
            <div className="flex items-center gap-2">
              <Check className="size-4 text-primary shrink-0" />
              <span>Direct Senior Consultant Access</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="size-4 text-primary shrink-0" />
              <span>Tailored Cambridgeshire Strategy</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="size-4 text-primary shrink-0" />
              <span>Transparent Monthly Reporting</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}